// src/data/admins.js
// Firestore CRUD for the `hub_admins` collection — the Hub's runtime admin
// allowlist. Doc id is the lowercased @meltplan.com email.
//
// Doc shape: {
//   email:      string (lowercased),
//   role:       'super' | 'scoped',   // legacy docs w/o a role are treated as super
//   categories: string[],             // category keys a scoped admin may manage
//   teams:      string[],             // optional team limit (Third Party Tools only)
//   addedBy:    string,
//   addedAt:    Timestamp,
// }

import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const HUB_ADMINS_COLLECTION = 'hub_admins';

// Subscribe to the runtime admin list. Calls cb with an array of
// { email, addedBy, addedAt }. Returns an unsubscribe function. `onError`
// (optional) is called if the listener fails (e.g. a permission-denied read);
// without it the failure is swallowed silently.
export function subscribeAdmins(cb, onError) {
  return onSnapshot(
    collection(db, HUB_ADMINS_COLLECTION),
    (snapshot) => {
      cb(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    (err) => {
      console.error('subscribeAdmins failed:', err);
      if (onError) onError(err);
    }
  );
}

// Add (or re-affirm) a Hub admin. Email is lowercased and used as the doc id.
// `options` = { role: 'super' | 'scoped', categories: string[], teams: string[] }.
export async function addAdmin(email, addedBy, options = {}) {
  const normalized = (email || '').trim().toLowerCase();
  const role = options.role === 'scoped' ? 'scoped' : 'super';
  await setDoc(doc(db, HUB_ADMINS_COLLECTION, normalized), {
    email: normalized,
    role,
    categories: role === 'scoped' ? options.categories || [] : [],
    teams: role === 'scoped' ? options.teams || [] : [],
    addedBy: (addedBy || '').toLowerCase(),
    addedAt: serverTimestamp(),
  });
}

// Update an existing admin's role/scope, leaving addedBy/addedAt intact.
export async function updateAdmin(email, options = {}) {
  const normalized = (email || '').trim().toLowerCase();
  const role = options.role === 'scoped' ? 'scoped' : 'super';
  await setDoc(
    doc(db, HUB_ADMINS_COLLECTION, normalized),
    {
      email: normalized,
      role,
      categories: role === 'scoped' ? options.categories || [] : [],
      teams: role === 'scoped' ? options.teams || [] : [],
    },
    { merge: true }
  );
}

export async function removeAdmin(email) {
  const normalized = (email || '').trim().toLowerCase();
  await deleteDoc(doc(db, HUB_ADMINS_COLLECTION, normalized));
}
